import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";

// Low-profile bottom-anchored CTA that fades + slides up only AFTER the user has
// scrolled past the hero section (default ~80vh). Mobile-only via Tailwind's
// `md:hidden` so desktop UX stays untouched. Click triggers `onPlan` (parent passes
// the same handler that opens the AIChat modal).
//
// Implementation notes:
//   - Listens via passive scroll listener — zero jank.
//   - Auto-hides when the user reaches the very bottom of the page so it never sits
//     on top of the footer's own CTA.
//   - Slides in with translate-y so it never causes a re-flow on mount.
export default function StickyMobileCta({ onPlan, label = "Plan My Trip", showAfter = 600 }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset;
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      const nearBottom = y > docH - 160;
      setShow(y > showAfter && !nearBottom);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [showAfter]);

  return (
    <div
      className={`md:hidden fixed inset-x-0 z-40 px-4 pointer-events-none transition-[transform,opacity] duration-300 ease-out ${
        show
          ? "translate-y-0 opacity-100 pointer-events-auto"
          : "translate-y-[120%] opacity-0"
      }`}
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 14px)" }}
      data-testid="sticky-mobile-cta"
    >
      <button
        type="button"
        onClick={onPlan}
        className="w-full inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-full bg-[var(--vp-primary)] hover:bg-[#0e1b3d] text-white font-bold tracking-[0.12em] uppercase text-[13px] shadow-[0_18px_40px_rgba(15,30,66,0.45)] ring-2 ring-[var(--vp-accent)]/40"
        data-testid="sticky-mobile-cta-btn"
      >
        <Sparkles className="w-4 h-4 text-[var(--vp-accent)]" />
        {label}
      </button>
      <div className="text-center text-[10px] text-white mix-blend-difference mt-1.5 tracking-wide opacity-80">
        Verified by human travel experts · 60 sec
      </div>
    </div>
  );
}
